import { useState } from "react";

import {
  auditStamp,
  MCP_CLIENTS_PATH,
  MCP_CLIENTS_REQUESTED_PATH,
  mcpClientsOf,
  redirectHost,
  requestedAppsOf,
  type McpClients,
  type McpRequestedApps,
} from "../../api/mcp";
import { AppAccessControl } from "../../components/mcp/AppAccessControl";
import { ResourceView } from "../../components/states";
import { Badge, Card, Empty, Notice, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Input } from "../../f7";
import { useResource } from "../../hooks/useResource";

/**
 * Which AI apps may connect to MCP.
 *
 * An app is allowed by its REDIRECT URI: the address MCP sends a merchant's
 * sign-in result to. Allowing one is a decision to hand merchants' tokens to
 * whoever controls that address, so this page is about addresses, not names —
 * a name is whatever the app says it is.
 *
 * Three parts, kept apart on purpose:
 *  - the baseline (Claude, ChatGPT, local desktop apps) is fixed in MCP's
 *    config and read-only here;
 *  - "added by admins" is the only list this page writes, live on every MCP
 *    server within ~60s, no restart;
 *  - "requested" is what MCP refused because its address was not allowed. It is
 *    the demand signal: nothing in it is allowed until someone allows it, and an
 *    app leaves it the moment it is.
 *
 * Removing an app blocks NEW sign-ins only; merchants already connected through
 * it keep working (MCP's decision, and the dialog says so).
 */
/**
 * MCP's sign-in allowlist, as a section of Policy → Settings.
 *
 * "Which apps may connect" is a setting, and Policy's tabs are what a change
 * applies to, not one tab per knob. `/policy/apps` redirects here. The
 * surrounding Card carries the title, so there is no heading of its own.
 */
export function AiAppsPanel({ config }: { config: AppConfig }) {
  const clients = useResource<McpClients>(config, MCP_CLIENTS_PATH);
  const requested = useResource<McpRequestedApps>(config, MCP_CLIENTS_REQUESTED_PATH);
  const [typed, setTyped] = useState("");

  // Both lists change on every write: an allowed app leaves "requested".
  function reloadAll() {
    clients.reload();
    requested.reload();
  }

  const typedUri = typed.trim();

  return (
    <>
      <ResourceView state={clients.state} what="the allowed apps" onRetry={clients.reload} />
      {clients.state.kind === "ready" &&
        (() => {
          const c = mcpClientsOf(clients.state.data);
          const full = c.extra.length >= c.maxExtra;
          return (
            <>
              <Card title="Always allowed" readOnly note="Fixed in MCP's config; not editable here.">
                <ul className="space-y-1">
                  {c.baseline.map((uri) => (
                    <li key={uri} className="break-all">
                      <span className="ref">{uri}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card
                title="Added by admins"
                aside={
                  <span className="font-mono text-xs tabular-nums text-slate-500 dark:text-slate-400">
                    {c.extra.length} of {c.maxExtra}
                  </span>
                }
              >
                {c.extra.length === 0 ? (
                  <Empty>No apps added. Only the apps above can connect.</Empty>
                ) : (
                  <Table
                    head={
                      <tr>
                        <Th>App</Th>
                        <Th>Redirect URI</Th>
                        <Th right>{""}</Th>
                      </tr>
                    }
                  >
                    {c.extra.map((uri) => (
                      <tr key={uri}>
                        <Td>{redirectHost(uri)}</Td>
                        <Td>
                          <span className="ref break-all">{uri}</span>
                        </Td>
                        <Td right>
                          <AppAccessControl config={config} uri={uri} mode="remove" onDone={reloadAll} />
                        </Td>
                      </tr>
                    ))}
                  </Table>
                )}

                <div className="mt-4 space-y-2">
                  <p className="text-sm">
                    Add an app by its redirect URI (https, one exact host, with the callback path).
                  </p>
                  {full ? (
                    <Notice tone="amber">
                      The list is full ({c.maxExtra} apps). Remove one before adding another.
                    </Notice>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="w-96 max-w-full">
                        <Input
                          value={typed}
                          onChange={(ev) => setTyped(ev.target.value)}
                          placeholder="https://app.example.com/oauth/callback"
                          aria-label="Redirect URI to allow"
                          maxLength={300}
                        />
                      </div>
                      {typedUri && (
                        <AppAccessControl
                          config={config}
                          uri={typedUri}
                          mode="allow"
                          label="Allow…"
                          onDone={() => {
                            setTyped("");
                            reloadAll();
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </>
          );
        })()}

      <Card
        title="Requested apps"
        note="Apps that tried to connect and were refused because their address is not allowed. Nothing here is allowed automatically."
      >
        <ResourceView state={requested.state} what="the requested apps" onRetry={requested.reload} />
        {requested.state.kind === "ready" &&
          (() => {
            const apps = requestedAppsOf(requested.state.data);
            if (apps.length === 0) return <Empty>No app has been refused recently.</Empty>;
            return (
              <Table
                head={
                  <tr>
                    <Th>App</Th>
                    <Th>Redirect URI</Th>
                    <Th right>Tries</Th>
                    <Th>Last tried</Th>
                    <Th right>{""}</Th>
                  </tr>
                }
              >
                {apps.map((a) => (
                  <tr key={a.redirectUri}>
                    <Td>
                      <div>{a.name ?? redirectHost(a.redirectUri)}</div>
                      {!a.knownHost && <Badge tone="amber">unfamiliar domain</Badge>}
                    </Td>
                    <Td>
                      <span className="ref break-all">{a.redirectUri}</span>
                    </Td>
                    <Td right>{a.tries}</Td>
                    <Td>{a.lastSeen ? auditStamp(a.lastSeen) : "—"}</Td>
                    <Td right>
                      {a.canAllow ? (
                        <AppAccessControl
                          config={config}
                          uri={a.redirectUri}
                          mode="allow"
                          knownHost={a.knownHost}
                          onDone={reloadAll}
                        />
                      ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          Cannot be allowed: {a.problem ?? "not a safe address"}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
            );
          })()}
      </Card>
    </>
  );
}
